import { z } from 'zod';

const Email = z.string().email().max(254);
const Username = z.string().regex(/^[a-zA-Z0-9_-]{2,32}$/, 'Username must be 2-32 chars: letters, digits, _ or -');
const Password = z.string().min(10, 'Password must be at least 10 characters').max(128);
const Name = z.string().min(1).max(80);
const Token = z.string().min(8).max(512);
const TotpCode = z.string().regex(/^\d{6}$/, 'TOTP code must be 6 digits');

// ── request schemas ──────────────────────────────────────────────────────────
export const SignupSchema = z.object({
  email: Email,
  username: Username,
  name: Name,
  password: Password,
  /** Optional invite-link token (`/register?ref=<token>`).  When valid +
   *  unused, the signup tx marks it consumed and writes reciprocal
   *  'invited' edges in `core.user_contacts` so inviter and signup see
   *  each other in their contact lists immediately. */
  referralToken: z.string().min(8).max(128).optional(),
  /** Team invitation token. Required for account creation when public
   * registration is disabled by either the environment or DB feature flag. */
  invitationToken: z.string().min(8).max(512).optional(),
  /** A UI language picked by hand before sign-up (`users.locale`); unknown → not chosen. */
  locale: z.string().max(8).optional(),
  /** The browser language the sign-up page was shown in (`users.detected_locale`). */
  detectedLocale: z.string().max(8).optional(),
  /** Bot checks (audit 2026-10): a visually hidden field people never fill… */
  honeypot: z.string().max(200).optional(),
  /** …the signed start-of-form token from `GET /api/auth/signup/challenge`… */
  formToken: z.string().max(300).optional(),
  /** …and the CAPTCHA response when the operator turned one on. */
  captchaToken: z.string().max(4096).optional(),
});
export type SignupInput = z.infer<typeof SignupSchema>;

export const SigninSchema = z.object({
  email: Email,
  // Sign-in only checks the password against the stored hash — it must NOT
  // re-apply the signup strength rule (10+ chars), or an older/short password
  // gets a 400 instead of a normal 401 and the user can never sign in.
  password: z.string().min(1).max(128),
});
export type SigninInput = z.infer<typeof SigninSchema>;

export const Signin2faSchema = z.object({
  ticket: Token,
  code: TotpCode,
});
export type Signin2faInput = z.infer<typeof Signin2faSchema>;

/** OAuth step-up tickets stay in an HttpOnly cookie; the browser submits only the factor. */
export const GithubStepUpSchema = z.object({ code: TotpCode });
export type GithubStepUpInput = z.infer<typeof GithubStepUpSchema>;

export const GithubRecoveryStepUpSchema = z.object({
  recoveryCode: z.string().min(8).max(64),
});
export type GithubRecoveryStepUpInput = z.infer<typeof GithubRecoveryStepUpSchema>;

export const RefreshSchema = z.object({
  // The refresh token travels exclusively in an httpOnly cookie
  // (`projoint_rt` / `projoint_admin_rt`) — never in the body, so JS can't
  // read or exfiltrate it.  The request body is empty; the cookie is the
  // sole transport for both web and admin.
});
export type RefreshInput = z.infer<typeof RefreshSchema>;

export const LogoutSchema = z.object({
  allSessions: z.boolean().optional().default(false),
});
export type LogoutInput = z.infer<typeof LogoutSchema>;

export const ConfirmEmailSchema = z.object({
  token: Token,
});
export type ConfirmEmailInput = z.infer<typeof ConfirmEmailSchema>;

export const RequestPasswordResetSchema = z.object({
  email: Email,
});
export type RequestPasswordResetInput = z.infer<typeof RequestPasswordResetSchema>;

export const ConfirmPasswordResetSchema = z.object({
  token: Token,
  newPassword: Password,
});
export type ConfirmPasswordResetInput = z.infer<typeof ConfirmPasswordResetSchema>;

export const ChangePasswordSchema = z.object({
  currentPassword: Password,
  newPassword: Password,
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;

/** Request an email change — current password gates it; the verification link
 *  is sent to the NEW address to prove ownership before the swap. */
export const RequestEmailChangeSchema = z.object({
  newEmail: Email,
  currentPassword: Password,
});
export type RequestEmailChangeInput = z.infer<typeof RequestEmailChangeSchema>;

/** Confirm an email change from the link sent to the new address. */
export const ConfirmEmailChangeSchema = z.object({
  token: Token,
});
export type ConfirmEmailChangeInput = z.infer<typeof ConfirmEmailChangeSchema>;

/** Confirm response — the new (now active) email so the UI can reflect it. */
export const ConfirmEmailChangeResultSchema = z.object({
  email: z.string(),
});
export type ConfirmEmailChangeResult = z.infer<typeof ConfirmEmailChangeResultSchema>;

export const DeleteAccountSchema = z.object({
  /** Required when the account has a password. Accounts that only sign in with
   *  GitHub have none — a recent sign-in (SensitiveSessionGuard) is the proof.
   *  No strength rule here: an older, shorter password must still work. */
  password: z.string().min(1).max(128).optional(),
  /** Client must echo the literal string "DELETE" as a second guard. */
  confirmation: z.literal('DELETE'),
});
export type DeleteAccountInput = z.infer<typeof DeleteAccountSchema>;

export const Confirm2faSchema = z.object({
  code: TotpCode,
});
export type Confirm2faInput = z.infer<typeof Confirm2faSchema>;

export const Enroll2faSchema = z.object({
  /** Required when replacing an already-registered factor. */
  currentCode: TotpCode.optional(),
});
export type Enroll2faInput = z.infer<typeof Enroll2faSchema>;

/** A recovery code is the dash-formatted `xxxxx-xxxxx` (case/format-insensitive
 *  on the server, which normalises before hashing). */
const RecoveryCode = z.string().min(8).max(40);

export const Signin2faRecoverySchema = z.object({
  ticket: Token,
  recoveryCode: RecoveryCode,
});
export type Signin2faRecoveryInput = z.infer<typeof Signin2faRecoverySchema>;

/** Response carrying a freshly-issued recovery-code set (shown once). */
export const BackupCodesSchema = z.object({
  backupCodes: z.array(z.string()),
});
export type BackupCodes = z.infer<typeof BackupCodesSchema>;

export const RecoveryCodesStatusSchema = z.object({
  remaining: z.number().int().nonnegative(),
});
export type RecoveryCodesStatus = z.infer<typeof RecoveryCodesStatusSchema>;

// ── response schemas ─────────────────────────────────────────────────────────
export const AuthenticatedUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  username: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** Chosen initials-avatar colour id (AVATAR_COLOR_IDS); null = neutral. */
  avatarColor: z.string().nullable().optional(),
  emailVerifiedAt: z.date().nullable(),
  twoFactorEnabled: z.boolean(),
});
export type AuthenticatedUser = z.infer<typeof AuthenticatedUserSchema>;

export const SignedInSchema = z.object({
  user: AuthenticatedUserSchema,
  accessToken: z.string(),
  // No `refreshToken` — it is set as an httpOnly cookie, never returned in the
  // response body (a JS-readable refresh token is an XSS exfiltration target).
});
export type SignedIn = z.infer<typeof SignedInSchema>;

export const TwoFactorRequiredSchema = z.object({
  twoFactorRequired: z.literal(true),
  ticket: z.string(),
});
export type TwoFactorRequired = z.infer<typeof TwoFactorRequiredSchema>;

/**
 * Returned by /signin when the user has NO 2FA yet but belongs to a team that
 * requires it (`teams.require_2fa`).  The client must run the TOTP enrolment
 * flow (enroll-required → enroll-required/confirm) before it can finish
 * signing in — there is no straight-through path for these accounts.
 */
export const EnrollmentRequiredSchema = z.object({
  enrollmentRequired: z.literal(true),
  ticket: z.string(),
});
export type EnrollmentRequired = z.infer<typeof EnrollmentRequiredSchema>;

export const SigninResponseSchema = z.union([
  SignedInSchema,
  TwoFactorRequiredSchema,
  EnrollmentRequiredSchema,
]);
export type SigninResponse = z.infer<typeof SigninResponseSchema>;

/** Body for the login-time 2FA enrolment endpoints (ticket-authenticated, no
 *  session yet). */
export const EnrollRequiredSchema = z.object({ ticket: Token });
export type EnrollRequiredInput = z.infer<typeof EnrollRequiredSchema>;

export const ConfirmEnrollRequiredSchema = z.object({ ticket: Token, code: TotpCode });
export type ConfirmEnrollRequiredInput = z.infer<typeof ConfirmEnrollRequiredSchema>;

export const RefreshResponseSchema = z.object({
  accessToken: z.string(),
  // The rotated refresh token is delivered as a fresh httpOnly cookie via
  // `Set-Cookie`, not in the body.
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const Enrol2faResponseSchema = z.object({
  otpauthUrl: z.string(),
  qrCodePng: z.string(),
  secret: z.string(),
});
export type Enrol2faResponse = z.infer<typeof Enrol2faResponseSchema>;

/** `GET /api/auth/signup/challenge` — what the sign-up form needs before submit. */
export const SignupChallengeSchema = z.object({
  formToken: z.string(),
  minFillMs: z.number().int().nonnegative(),
  captcha: z
    .object({ provider: z.enum(['turnstile', 'hcaptcha']), siteKey: z.string() })
    .nullable(),
});
export type SignupChallenge = z.infer<typeof SignupChallengeSchema>;
